import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { updateMetaTags } from '../utils/seo';
import { SKILL_BUNDLES, TIME, LOCATION, buildTitle, buildDescription, canonicalFor } from './idea-landing-config';
import ReadingProgress from '../components/common/ReadingProgress';
import StickyTOC from '../components/common/StickyTOC';
import IncomeEstimator from '../components/common/IncomeEstimator';
import PricingSection from '../components/Home/PricingSection';

const IdeaLanding: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { skillsSlug, timeSlug, locationSlug } = params as Record<string, string>;

  const resolved = useMemo(() => {
    const skills = SKILL_BUNDLES[skillsSlug];
    const time = TIME[timeSlug];
    const location = LOCATION[locationSlug];
    return { skills, time, location };
  }, [skillsSlug, timeSlug, locationSlug]);

  // If invalid slug, send to home
  useEffect(() => {
    if (!resolved.skills || !resolved.time || !resolved.location) {
      navigate('/');
    }
  }, [resolved, navigate]);

  // SEO meta tags (no external libs)
  useEffect(() => {
    if (!resolved.skills || !resolved.time || !resolved.location) return;
    const title = buildTitle(resolved.skills.title, resolved.time.label, resolved.location.label);
    const description = buildDescription(resolved.skills.title, resolved.time.label, resolved.location.label);
    const url = canonicalFor(skillsSlug, timeSlug, locationSlug);
    // Primary canonical: weekend + remote to consolidate thin variants
    const isPrimary = timeSlug === 'weekend' && locationSlug === 'remote';
    const canonicalUrl = isPrimary ? url : canonicalFor(skillsSlug, 'weekend', 'remote');
    updateMetaTags({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogUrl: url,
      twitterUrl: url,
      canonicalUrl,
      ogImage: 'https://explanatum.co.uk/og-image.jpg',
      robots: isPrimary ? 'index,follow' : 'noindex,follow',
    });
    // Analytics: page view
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'view_idea_landing', {
          page_location: url,
          skills_slug: skillsSlug,
          time_slug: timeSlug,
          location_slug: locationSlug
        });
      }
    } catch {}
  }, [skillsSlug, timeSlug, locationSlug, resolved]);

  if (!resolved.skills || !resolved.time || !resolved.location) {
    return null;
  }

  const prefillQuery = new URLSearchParams({
    skills: resolved.skills.ids.join(','),
    time: resolved.time.id,
    work: resolved.location.ids.join(','),
    scope: resolved.location.scope
  }).toString();

  const startHref = `/idea-generator?${prefillQuery}`;

  const onStartClick = () => {
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'click_start_generator_prefilled', {
          source: 'idea_landing',
          skills_slug: skillsSlug,
          time_slug: timeSlug,
          location_slug: locationSlug
        });
      }
    } catch {}
  };

  // JSON-LD structured data for better SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${resolved.skills.title} Business Ideas (${resolved.time.label}, ${resolved.location.label})`,
    description: buildDescription(resolved.skills.title, resolved.time.label, resolved.location.label),
    url: canonicalFor(skillsSlug!, timeSlug!, locationSlug!),
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://explanatum.co.uk/' },
        { '@type': 'ListItem', position: 2, name: 'Business Ideas', item: 'https://explanatum.co.uk/business-ideas' },
        { '@type': 'ListItem', position: 3, name: `${resolved.skills.title} • ${resolved.time.label} • ${resolved.location.label}` }
      ]
    }
  };

  const tocItems = [
    { id: 'start-generator', label: 'Start with AI Generator' },
    { id: 'ai-matching', label: 'How AI Matches Your Skills' },
    { id: 'popular-variations', label: 'Popular Variations' },
    { id: 'related-skills', label: 'Related Skills' },
    { id: 'free-trial', label: '7‑Day Free Trial' },
  ];

  return (
    <main className="min-h-screen relative overflow-hidden">
      <ReadingProgress />
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-gray-50 via-white to-indigo-50" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-200/30 blur-3xl" />

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:flex lg:gap-6">
        <StickyTOC items={tocItems} />
        <div className="min-w-0 flex-1">
        <header className="mb-10">
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center gap-2 text-sm text-gray-600">
              <li>
                <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              </li>
              <li aria-hidden="true" className="text-gray-400">/</li>
              <li>
                <Link to="/business-ideas" className="hover:text-gray-900 transition-colors">Business Ideas</Link>
              </li>
              <li aria-hidden="true" className="text-gray-400">/</li>
              <li aria-current="page" className="font-semibold text-gray-800">
                {resolved.skills.title} • {resolved.time.label} • {resolved.location.label}
              </li>
            </ol>
          </nav>

          <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white/70 backdrop-blur-sm shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5" />
            <div className="relative p-6 sm:p-10">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                <span className="bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                  {resolved.skills.title} Business Ideas
                </span>
                <span className="block text-gray-800 mt-1 text-xl sm:text-2xl">
                  for {resolved.time.label} • {resolved.location.label}
                </span>
              </h1>
              <p className="mt-4 text-lg text-gray-700 max-w-3xl">
                AI that matches <strong>all your skills</strong> to refined opportunities—so you can ship faster, earn sooner, and choose the model that fits your life. These ideas are tuned for {resolved.time.label.toLowerCase()} creators who prefer {resolved.location.label.toLowerCase()} work.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-sm">{resolved.skills.title}</span>
                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-sm">{resolved.time.label}</span>
                <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1 text-sm">{resolved.location.label}</span>
              </div>
            </div>
          </div>
        </header>

        <section aria-labelledby="start-generator" id="start-generator" className="my-10">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-200/70 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-blue-500/5" />
            <div className="relative p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h2 id="start-generator" className="text-xl font-semibold text-gray-900">Generate tailored ideas</h2>
                  <p className="text-gray-700 mt-1">Explanatum uses AI to map your skill mix to <em>validated opportunities</em> with steps, pricing, and outreach. Tweak skills and constraints in the wizard.</p>
                </div>
                <Link
                  to={startHref}
                  onClick={onStartClick}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow hover:shadow-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path d="M2.25 12c0-.41.25-.78.63-.93l16.5-6.75a.97.97 0 0 1 1.32 1.08l-2.31 9.24a.97.97 0 0 1-.58.68l-6.15 2.46 2.46 6.15a.97.97 0 0 1-1.66.98l-6.75-16.5A.96.96 0 0 1 2.25 12Z" />
                  </svg>
                  Start Idea Generator
                </Link>
              </div>
            </div>
            <div className="mt-6">
              <IncomeEstimator />
            </div>
          </div>
        </section>

        {/* AI matching explainer */}
        <section aria-labelledby="ai-matching" id="ai-matching" className="my-10">
          <div className="relative overflow-hidden rounded-2xl bg-white/70 border border-gray-200/60 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5" />
            <div className="relative p-6 sm:p-8">
              <h2 id="ai-matching" className="text-xl font-semibold text-gray-900">How the AI matches your skills to opportunity</h2>
              <ul className="mt-3 grid sm:grid-cols-2 gap-3 text-gray-700">
                <li className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" /><span>Maps {resolved.skills.title.toLowerCase()} to proven business models (freelance, productized, SaaS, content, education).</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" /><span>Surfaces fast-start pathways for {resolved.time.label.toLowerCase()} and {resolved.location.label.toLowerCase()} setups.</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" /><span>Generates steps, pricing bands, and first outreach angles.</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" /><span>Expands with 7,000+ adjacent skills to unlock bigger opportunities over time.</span></li>
              </ul>
            </div>
          </div>
        </section>

        

        

        

        {/* Unified 7‑day free trial signup (placed above variations) */}
        <section aria-labelledby="free-trial" id="free-trial" className="my-14">
          <PricingSection />
        </section>

        <section aria-labelledby="popular-variations" id="popular-variations" className="my-10">
          <h2 id="popular-variations" className="text-xl font-semibold text-gray-900 mb-4">Popular variations</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(TIME).map(([slug, t]) => (
              <li key={slug}>
                <Link
                  to={`/business-ideas/${skillsSlug}/${slug}/${locationSlug}`}
                  className="group block relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition" />
                  <div className="relative z-10">
                    <p className="font-medium text-gray-900">{resolved.skills.title} • {t.label} • {resolved.location.label}</p>
                    <p className="mt-1 text-sm text-gray-600">Try this time commitment with the same skills and location.</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <aside aria-labelledby="related-skills" id="related-skills" className="my-10">
          <h2 id="related-skills" className="text-xl font-semibold text-gray-900 mb-4">Explore related skill sets</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(SKILL_BUNDLES).map(([slug, s]) => (
              <li key={slug}>
                <Link
                  to={`/business-ideas/${slug}/${timeSlug}/${locationSlug}`}
                  className="group block relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition" />
                  <div className="relative z-10">
                    <p className="font-medium text-gray-900">{s.title} • {resolved.time.label} • {resolved.location.label}</p>
                    <p className="mt-1 text-sm text-gray-600">Browse similar skill bundles with your current constraints.</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        

        {/* Hidden semantic helper content for SEO */}
        <section aria-hidden="true" className="sr-only">
          <h2>Semantic topics</h2>
          <p>business ideas, {resolved.skills.title.toLowerCase()}, {resolved.time.label.toLowerCase()}, {resolved.location.label.toLowerCase()}, side hustle, startup ideas</p>
        </section>
        </div>
      </article>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </main>
  );
};

export default IdeaLanding;
